interface DownloadFileArgs {
  url: string;
  filename: string;
}

/**
 * CMS urls are authored inconsistently: some are absolute, some are app routes,
 * and some are bare hosts like "google.com" that would otherwise resolve
 * against the app origin. Bare hosts are promoted to https.
 */
export const resolveCmsUrl = (url: string): string => {
  const trimmed = url?.trim() ?? '';
  if (!trimmed) return trimmed;
  if (/^(https?:|blob:|data:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) return trimmed;
  // Looks like "example.com" or "example.com/file.pdf" rather than a route.
  if (/^[\w-]+(\.[\w-]+)+(\/|$|\?)/.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
};

export const downloadFile = ({ url, filename }: DownloadFileArgs) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  // Firefox only honours the click when the anchor is in the document.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Downloads a file from an arbitrary url.
 *
 * The `download` attribute is ignored for cross-origin urls, so a remote file
 * is fetched into a blob first and saved from a same-origin object url. Falls
 * back to opening the file when the fetch is blocked (e.g. missing CORS).
 */
export const downloadRemoteFile = async ({ url, filename }: DownloadFileArgs) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Request failed with ${response.status}`);

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    downloadFile({ url: objectUrl, filename });
    window.URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export const downloadFileFromCms = ({ url, filename }: DownloadFileArgs) => {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.download = filename;
  link.click();
};
